import { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { FiberManualRecord as BulletIcon } from '@mui/icons-material';

interface ResetPasswordFormProps {
  onResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
  defaultEmail?: string;
}

export function ResetPasswordForm({
  onResetPassword,
  isLoading,
  error,
  onClearError,
  defaultEmail = '',
}: ResetPasswordFormProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    onClearError();

    if (!email) {
      setValidationError('Please enter your email');
      return;
    }

    if (!code.trim()) {
      setValidationError('Please enter the verification code');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setValidationError('Please enter and confirm your new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    const hasUpper = /[A-Z]/.test(newPassword);
    const hasLower = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSymbol = /[\W_]/.test(newPassword);
    if (!hasUpper || !hasLower || !hasNumber || !hasSymbol) {
      setValidationError('Password must include uppercase, lowercase, numbers, and symbols');
      return;
    }

    await onResetPassword(email, code.trim(), newPassword);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 400,
        mx: 'auto',
        p: 4,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography variant="h5" component="h2" align="center" mb={2}>
        Reset Your Password
      </Typography>

      <Typography variant="body1" color="text.secondary" align="center" mb={2}>
        Enter the verification code sent to your email along with your new password.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading || !!defaultEmail}
          placeholder="Enter your email"
          margin="normal"
          variant="outlined"
        />

        <TextField
          fullWidth
          id="code"
          label="Verification Code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={isLoading}
          placeholder="Enter verification code"
          margin="normal"
          variant="outlined"
          autoFocus
        />

        <TextField
          fullWidth
          id="newPassword"
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isLoading}
          placeholder="Enter new password"
          margin="normal"
          variant="outlined"
        />

        <TextField
          fullWidth
          id="confirmPassword"
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          placeholder="Confirm new password"
          margin="normal"
          variant="outlined"
          sx={{ mb: 2 }}
        />

        {(error || validationError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || validationError}
          </Alert>
        )}

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Password requirements:
          </Typography>
          <List dense sx={{ pt: 0 }}>
            <ListItem sx={{ py: 0 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <BulletIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText
                primary="At least 8 characters long"
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <BulletIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText
                primary="Should include a mix of uppercase, lowercase, numbers, and symbols"
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItem>
          </List>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            position: 'relative',
            height: 48,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
              Resetting Password...
            </>
          ) : (
            'Reset Password'
          )}
        </Button>
      </Box>
    </Paper>
  );
}
